<!-- The legal HTML is generated from controlled DOCX files and sanitized with DOMPurify. -->
<!-- eslint-disable vue/no-v-html -->
<template>
  <f7-page name="LegalDocuments" class="legal-page" :lang="locale">
    <header class="legal-header">
      <div class="legal-header__inner">
        <a
          :href="appEntryUrl"
          class="legal-brand external"
          :aria-label="copy.goToHome"
        >
          <img :src="APP_LOGO" :alt="APP_NAME" />
        </a>
        <a :href="appEntryUrl" class="legal-header__access external">
          {{ copy.accessBesafer }}
        </a>
      </div>
    </header>

    <main id="contenido-legal" class="legal-layout">
      <section class="legal-hero" aria-labelledby="legal-title">
        <p class="legal-eyebrow">{{ copy.legalInformation }}</p>
        <h1 id="legal-title">{{ copy.pageTitle }}</h1>
        <!-- <p class="legal-hero__description">
          En esta página puedes consultar de forma pública y continua
          las políticas y condiciones aplicables al uso de Besafer.
        </p> -->
      </section>

      <div class="legal-content">
        <nav class="legal-index" :aria-label="copy.legalIndexLabel">
          <p class="legal-index__title">{{ copy.onThisPage }}</p>
          <ol>
            <li v-for="document in documents" :key="document.id">
              <a
                :href="`#${document.id}`"
                @click.prevent="scrollToSection(document.id)"
              >
                {{ document.shortTitle }}
              </a>
            </li>
          </ol>
        </nav>

        <div class="legal-documents" @click="handleLegalContentClick">
          <article
            v-for="(document, index) in documents"
            :id="document.id"
            :key="document.id"
            class="legal-document"
            :aria-labelledby="`${document.id}-title`"
          >
            <p class="legal-document__number">
              {{ copy.document }} {{ index + 1 }} {{ copy.of }}
              {{ documents.length }}
            </p>
            <h2 :id="`${document.id}-title`">
              {{ document.title }}
            </h2>

            <nav
              class="legal-section-index"
              :aria-label="`${copy.contentsOf} ${document.shortTitle}`"
            >
              <p>{{ copy.documentContents }}</p>
              <ul>
                <li
                  v-for="section in document.sections"
                  :key="`${section.id}-index`"
                >
                  <a
                    :href="`#${section.id}`"
                    @click.prevent="scrollToSection(section.id)"
                  >
                    {{ section.title }}
                  </a>
                </li>
              </ul>
            </nav>

            <section
              v-for="section in document.sections"
              :id="section.id"
              :key="section.id"
              class="legal-section"
              :aria-labelledby="`${section.id}-title`"
            >
              <h3 :id="`${section.id}-title`">
                {{ section.title }}
              </h3>

              <template v-for="(block, blockIndex) in section.blocks">
                <p
                  v-if="block.type === 'paragraph'"
                  :key="`${section.id}-paragraph-${blockIndex}`"
                  v-html="sanitizeLegalHtml(block.html)"
                ></p>

                <h4
                  v-else-if="block.type === 'subheading'"
                  :key="`${section.id}-subheading-${blockIndex}`"
                  v-html="sanitizeLegalHtml(block.html)"
                ></h4>

                <ul
                  v-else-if="block.type === 'list'"
                  :key="`${section.id}-list-${blockIndex}`"
                >
                  <li
                    v-for="(item, itemIndex) in block.items"
                    :key="`${section.id}-list-${blockIndex}-${itemIndex}`"
                    v-html="sanitizeLegalHtml(item)"
                  ></li>
                </ul>

                <div
                  v-else-if="block.type === 'table'"
                  :key="`${section.id}-table-${blockIndex}`"
                  class="legal-table-wrapper"
                >
                  <table>
                    <tbody>
                      <tr
                        v-for="(row, rowIndex) in block.rows"
                        :key="`${section.id}-row-${rowIndex}`"
                      >
                        <td
                          v-for="(cell, cellIndex) in row"
                          :key="`${section.id}-cell-${rowIndex}-${cellIndex}`"
                          v-html="sanitizeLegalHtml(cell)"
                        ></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>
            </section>
          </article>
        </div>
      </div>
    </main>

    <button
      v-show="showBackToTop"
      type="button"
      class="legal-back-to-top"
      :aria-label="copy.backToTopLabel"
      :title="copy.backToTop"
      @click="scrollToTop"
    >
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          d="M5 15l7-7 7 7"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <footer class="legal-footer">
      <div class="legal-footer__inner">
        <img :src="APP_LOGO" alt="" aria-hidden="true" />
        <p>{{ copy.footer }}</p>
      </div>
    </footer>
  </f7-page>
</template>

<script>
import { APP_NAME, APP_LOGO } from '@/js/constants';
import LegalDocumentsContentEs from '@/json/legalDocuments.json';
import LegalDocumentsContentEn from '@/json/legalDocuments.en.json';
import LegalDocumentsContentFr from '@/json/legalDocuments.fr.json';
import LegalDocumentsContentIt from '@/json/legalDocuments.it.json';
import LegalDocumentsContentPt from '@/json/legalDocuments.pt.json';
import { getLegalDocumentsLocale } from '@/js/legalDocumentsLocale';
import { canonicalizeLegalHash } from '@/js/legalDocumentsRoutes';
import { getAppEntryUrl } from '@/js/appEntryUrl';
import DOMPurify from 'dompurify';

const spanishLegalDocumentLabels = {
  'privacy-policy': {
    shortTitle: 'Política de privacidad',
    title: 'Política de privacidad de Besafer',
  },
  'terms-of-use': {
    shortTitle: 'Condiciones de uso',
    title: 'Condiciones de uso de Besafer',
  },
  'cookie-policy': {
    shortTitle: 'Política de cookies',
    title: 'Política de cookies y tecnologías similares de Besafer',
  },
  'information-security-policy': {
    shortTitle: 'Política de seguridad',
    title:
      'Política de seguridad de la información para clientes de Besafer',
  },
  'legal-notice': {
    shortTitle: 'Aviso legal',
    title: 'Aviso legal de Besafer',
  },
};

const pageCopies = {
  es: {
    goToHome: 'Ir al inicio de Besafer',
    accessBesafer: 'Acceder a Besafer',
    legalInformation: 'Información legal',
    pageTitle: 'Documentos legales de Besafer',
    legalIndexLabel: 'Índice de documentos legales',
    onThisPage: 'En esta página',
    document: 'Documento',
    of: 'de',
    contentsOf: 'Contenido de',
    documentContents: 'Contenido del documento',
    backToTopLabel: 'Volver al principio de la página',
    backToTop: 'Volver arriba',
    footer: 'Besafer · Información legal y protección de datos',
  },
  en: {
    goToHome: 'Go to the Besafer home page',
    accessBesafer: 'Access Besafer',
    legalInformation: 'Legal information',
    pageTitle: 'Besafer legal documents',
    legalIndexLabel: 'Legal documents index',
    onThisPage: 'On this page',
    document: 'Document',
    of: 'of',
    contentsOf: 'Contents of',
    documentContents: 'Document contents',
    backToTopLabel: 'Return to the top of the page',
    backToTop: 'Back to top',
    footer: 'Besafer · Legal information and data protection',
  },
  fr: {
    goToHome: 'Aller à l’accueil de Besafer',
    accessBesafer: 'Accéder à Besafer',
    legalInformation: 'Informations légales',
    pageTitle: 'Documents légaux de Besafer',
    legalIndexLabel: 'Index des documents légaux',
    onThisPage: 'Sur cette page',
    document: 'Document',
    of: 'sur',
    contentsOf: 'Contenu de',
    documentContents: 'Contenu du document',
    backToTopLabel: 'Revenir en haut de la page',
    backToTop: 'Retour en haut',
    footer:
      'Besafer · Informations légales et protection des données',
  },
  it: {
    goToHome: 'Vai alla pagina iniziale di Besafer',
    accessBesafer: 'Accedi a Besafer',
    legalInformation: 'Informazioni legali',
    pageTitle: 'Documenti legali di Besafer',
    legalIndexLabel: 'Indice dei documenti legali',
    onThisPage: 'In questa pagina',
    document: 'Documento',
    of: 'di',
    contentsOf: 'Contenuto di',
    documentContents: 'Contenuto del documento',
    backToTopLabel: 'Torna all’inizio della pagina',
    backToTop: 'Torna su',
    footer: 'Besafer · Informazioni legali e protezione dei dati',
  },
  pt: {
    goToHome: 'Ir para a página inicial da Besafer',
    accessBesafer: 'Aceder à Besafer',
    legalInformation: 'Informações legais',
    pageTitle: 'Documentos legais da Besafer',
    legalIndexLabel: 'Índice dos documentos legais',
    onThisPage: 'Nesta página',
    document: 'Documento',
    of: 'de',
    contentsOf: 'Conteúdo de',
    documentContents: 'Conteúdo do documento',
    backToTopLabel: 'Voltar ao início da página',
    backToTop: 'Voltar ao início',
    footer: 'Besafer · Informações legais e proteção de dados',
  },
};

const legalDocumentsByLocale = {
  es: LegalDocumentsContentEs.map((document) => ({
    ...document,
    ...spanishLegalDocumentLabels[document.id],
  })),
  en: LegalDocumentsContentEn,
  fr: LegalDocumentsContentFr,
  it: LegalDocumentsContentIt,
  pt: LegalDocumentsContentPt,
};

export default {
  name: 'LegalDocumentsPage',
  data() {
    const locale = getLegalDocumentsLocale();

    return {
      APP_NAME,
      APP_LOGO,
      appEntryUrl: getAppEntryUrl(),
      locale,
      showBackToTop: false,
      scrollContainer: null,
      previousDocumentLanguage: '',
      previousDocumentTitle: '',
      i18nInstance: null,
    };
  },
  computed: {
    copy() {
      return pageCopies[this.locale];
    },
    documents() {
      return legalDocumentsByLocale[this.locale];
    },
  },
  mounted() {
    document.body.classList.add('legal-documents-active');

    this.previousDocumentLanguage =
      document.documentElement.getAttribute('lang') || '';
    this.previousDocumentTitle = document.title;

    this.i18nInstance = this.$i18n?.i18next || null;
    if (this.i18nInstance) {
      this.i18nInstance.on(
        'languageChanged',
        this.handleLanguageChange
      );
    }

    document.documentElement.setAttribute('lang', this.locale);
    document.title = this.copy.pageTitle;

    this.$nextTick(() => {
      this.scrollContainer = this.$el.querySelector('.page-content');

      if (this.scrollContainer) {
        this.scrollContainer.addEventListener(
          'scroll',
          this.handleScroll,
          { passive: true }
        );
      }

      this.scrollToHash();
    });
  },
  beforeDestroy() {
    document.body.classList.remove('legal-documents-active');

    if (this.i18nInstance) {
      this.i18nInstance.off(
        'languageChanged',
        this.handleLanguageChange
      );
    }

    if (this.previousDocumentLanguage) {
      document.documentElement.setAttribute(
        'lang',
        this.previousDocumentLanguage
      );
    } else {
      document.documentElement.removeAttribute('lang');
    }
    document.title = this.previousDocumentTitle;

    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener(
        'scroll',
        this.handleScroll
      );
    }
  },
  methods: {
    handleLanguageChange(language) {
      this.locale = getLegalDocumentsLocale(language);
      document.documentElement.setAttribute('lang', this.locale);
      document.title = this.copy.pageTitle;
    },
    sanitizeLegalHtml(html) {
      return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['a', 'br', 'em', 'strong', 'u'],
        ALLOWED_ATTR: ['href', 'rel', 'target'],
      });
    },
    handleLegalContentClick(event) {
      const link = event.target.closest('a[href^="#"]');
      if (!link) return;

      event.preventDefault();
      this.scrollToSection(link.getAttribute('href'));
    },
    handleScroll() {
      this.showBackToTop =
        (this.scrollContainer?.scrollTop || 0) > 400;
    },
    scrollToHash() {
      if (window.location.hash.startsWith('#/')) {
        const [, routeQuery = ''] = window.location.hash.split('?');
        const routePath = window.location.hash
          .slice(1)
          .split(/[?#]/)[0];
        // En modo publico la app se sirve como hash route. Los enlaces del
        // banner usan ?section=... para forzar carga de pagina y luego hacer
        // scroll al mismo documento que el indice lateral.
        const requestedSection = new URLSearchParams(
          routeQuery.split('#')[0]
        ).get('section');

        if (requestedSection) {
          window.setTimeout(() => {
            this.scrollToSection(requestedSection, false);
          }, 0);
          return;
        }

        if (routePath === '/legal-notice') {
          window.setTimeout(() => {
            this.scrollToSection('legal-notice', false);
          }, 0);
        }

        return;
      }

      if (window.location.pathname === '/legal-notice') {
        window.setTimeout(() => {
          this.scrollToSection('legal-notice', false);
        }, 0);
        return;
      }

      const requestedHash = window.location.hash.replace(/^#/, '');
      const sectionId = canonicalizeLegalHash(requestedHash);

      if (sectionId) {
        if (sectionId !== requestedHash) {
          window.history.replaceState(
            null,
            document.title,
            `${window.location.pathname}${window.location.search}#${sectionId}`
          );
        }

        window.setTimeout(() => {
          this.scrollToSection(sectionId, false);
        }, 0);
      }
    },
    scrollToSection(sectionId, updateHash = true) {
      const canonicalId = canonicalizeLegalHash(sectionId);
      const section = this.$el.querySelector(`#${canonicalId}`);
      if (!section) return;

      section.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      if (updateHash) {
        window.history.replaceState(
          null,
          document.title,
          `${window.location.pathname}${window.location.search}#${canonicalId}`
        );
      }
    },
    scrollToTop() {
      if (!this.scrollContainer) return;

      this.scrollContainer.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.legal-page {
  --legal-text: var(--his-berenjena, #2f4155);
  --legal-blue: var(--his-mora, #04509f);
  --legal-dark-blue: var(--his-arandano, #00346a);
  min-height: 100%;
  background: var(--his-mora-white, #ebf2fa);
  color: var(--legal-text);
}

.legal-page :deep(.page-content) {
  min-height: 100%;
  height: 100%;
  padding: 0 !important;
  display: flex;
  flex-direction: column;
  background: var(--his-mora-white, #ebf2fa);
  overflow-y: auto;
  scroll-behavior: smooth;
}

.legal-header {
  position: sticky;
  z-index: 20;
  top: 0;
  width: 100%;
  flex: 0 0 auto;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.96);
  border-bottom: 1px solid var(--his-disabled, #e5eef7);
  backdrop-filter: blur(10px);
}

.legal-header__inner,
.legal-footer__inner,
.legal-layout {
  width: 92%;
  margin-right: auto;
  margin-left: auto;
  box-sizing: border-box;
}

.legal-header__inner {
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.legal-brand {
  display: inline-flex;
  align-items: center;
}

.legal-brand img {
  display: block;
  width: 150px;
  height: auto;
}

.legal-header__access {
  color: var(--legal-blue);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
}

.legal-header__access:hover,
.legal-header__access:focus-visible {
  color: var(--legal-dark-blue);
  text-decoration: underline;
}

.legal-layout {
  flex: 1 0 auto;
  padding: 64px 0 96px;
}

.legal-hero {
  max-width: 820px;
  margin-bottom: 48px;
}

.legal-eyebrow,
.legal-document__number {
  margin: 0 0 10px;
  color: var(--legal-blue);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.legal-hero h1 {
  margin: 0;
  color: var(--legal-dark-blue);
  font-size: 48px;
  line-height: 1.08;
  letter-spacing: 0;
}

.legal-hero__description {
  max-width: 720px;
  margin: 22px 0 0;
  color: var(--legal-text);
  font-size: 18px;
  line-height: 1.65;
}

.legal-content {
  display: grid;
  grid-template-columns: minmax(230px, 300px) minmax(0, 1fr);
  gap: clamp(32px, 4vw, 72px);
  align-items: start;
}

.legal-index {
  position: sticky;
  top: 104px;
  padding: 24px;
  background: #fff;
  border-left: 4px solid var(--legal-blue);
  border-radius: 0 8px 8px 0;
  box-shadow: 0 4px 18px rgba(0, 52, 106, 0.05);
}

.legal-index__title {
  margin: 0 0 14px;
  color: var(--legal-dark-blue);
  font-size: 15px;
  font-weight: 700;
}

.legal-index ol {
  margin: 0;
  padding-left: 20px;
}

.legal-index li + li {
  margin-top: 12px;
}

.legal-index a {
  color: var(--legal-text);
  line-height: 1.45;
  text-decoration: none;
}

.legal-index a:hover,
.legal-index a:focus-visible {
  color: var(--legal-blue);
  text-decoration: underline;
}

.legal-documents {
  min-width: 0;
}

.legal-document {
  scroll-margin-top: 100px;
  padding: 40px clamp(32px, 4vw, 64px);
  background: #fff;
  border: 1px solid var(--his-disabled, #e5eef7);
  border-radius: 8px;
  box-shadow: 0 4px 18px rgba(0, 52, 106, 0.06);
}

.legal-document + .legal-document {
  margin-top: 28px;
}

.legal-document h2 {
  margin: 0 0 20px;
  color: var(--legal-dark-blue);
  font-size: 30px;
  line-height: 1.25;
}

.legal-section-index {
  margin: 28px 0 40px;
  padding: 22px 24px;
  background: var(--his-arandano-white, #f1f8ff);
  border: 1px solid var(--his-mora-tint, #d3e3f5);
  border-radius: 8px;
}

.legal-section-index p {
  margin: 0 0 14px !important;
  color: var(--legal-dark-blue);
  font-size: 14px !important;
  font-weight: 700;
}

.legal-section-index ul {
  margin: 0;
  padding: 0;
  column-count: 2;
  column-gap: 32px;
  list-style: none;
}

.legal-section-index li {
  break-inside: avoid;
  margin-bottom: 9px;
}

.legal-section-index a {
  color: var(--legal-blue);
  font-size: 14px;
  line-height: 1.45;
  text-decoration: none;
}

.legal-section-index a:hover,
.legal-section-index a:focus-visible {
  text-decoration: underline;
}

.legal-section {
  min-width: 0;
  scroll-margin-top: 100px;
  padding-top: 32px;
  border-top: 1px solid var(--his-disabled, #e5eef7);
}

.legal-section + .legal-section {
  margin-top: 32px;
}

.legal-section h3 {
  width: 100%;
  max-width: none;
  margin: 0 0 20px;
  color: var(--legal-dark-blue);
  font-size: 22px;
  line-height: 1.35;
}

.legal-section h4 {
  width: 100%;
  max-width: none;
  margin: 26px 0 10px;
  color: var(--legal-text);
  font-size: 16px;
  line-height: 1.45;
}

.legal-section p {
  width: 100%;
  max-width: none;
  margin: 0;
  font-size: 15px;
  line-height: 1.7;
  overflow-wrap: anywhere;
}

.legal-section p + p {
  margin-top: 14px;
}

.legal-section ul {
  width: 100%;
  max-width: none;
  margin: 14px 0 18px;
  padding-left: 25px;
  box-sizing: border-box;
}

.legal-section li {
  padding-left: 4px;
  font-size: 15px;
  line-height: 1.65;
  overflow-wrap: anywhere;
}

.legal-section li + li {
  margin-top: 7px;
}

.legal-section li::marker {
  color: var(--legal-blue);
}

.legal-section :deep(a) {
  color: var(--legal-blue);
  overflow-wrap: anywhere;
}

.legal-table-wrapper {
  max-width: 100%;
  margin: 18px 0;
  overflow-x: auto;
  border: 1px solid var(--his-disabled, #e5eef7);
  border-radius: 8px;
}

.legal-table-wrapper table {
  width: 100%;
  border-spacing: 0;
  border-collapse: collapse;
}

.legal-table-wrapper td {
  padding: 12px 14px;
  border-right: 1px solid var(--his-disabled, #e5eef7);
  border-bottom: 1px solid var(--his-disabled, #e5eef7);
  font-size: 14px;
  line-height: 1.5;
  vertical-align: top;
}

.legal-table-wrapper tr:last-child td {
  border-bottom: 0;
}

.legal-table-wrapper td:last-child {
  border-right: 0;
}

.legal-back-to-top {
  position: fixed;
  z-index: 40;
  right: 28px;
  bottom: 28px;
  width: 48px;
  height: 48px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: var(--legal-blue);
  border: 0;
  border-radius: 50%;
  box-shadow: 0 6px 20px rgba(0, 52, 106, 0.3);
  cursor: pointer;
}

.legal-back-to-top:hover,
.legal-back-to-top:focus-visible {
  background: var(--legal-dark-blue);
  outline: 2px solid #fff;
  outline-offset: 2px;
}

.legal-footer {
  width: 100%;
  padding: 34px 0;
  flex: 0 0 auto;
  box-sizing: border-box;
  color: #fff;
  background: var(--legal-dark-blue);
}

.legal-footer__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.legal-footer img {
  width: 112px;
  filter: brightness(0) invert(1);
}

.legal-footer p {
  margin: 0;
  font-size: 14px;
}

@media (max-width: 900px) {
  .legal-header__inner,
  .legal-footer__inner,
  .legal-layout {
    width: calc(100% - 32px);
  }

  .legal-header__inner {
    min-height: 64px;
  }

  .legal-brand img {
    width: 124px;
  }

  .legal-layout {
    padding: 40px 0 64px;
  }

  .legal-hero {
    margin-bottom: 32px;
  }

  .legal-hero h1 {
    font-size: 34px;
  }

  .legal-hero__description {
    font-size: 16px;
  }

  .legal-content {
    display: block;
  }

  .legal-index {
    position: static;
    margin-bottom: 24px;
  }

  .legal-document {
    padding: 28px 22px;
    scroll-margin-top: 82px;
  }

  .legal-section-index {
    margin-bottom: 32px;
    padding: 18px;
  }

  .legal-section-index ul {
    column-count: 1;
  }

  .legal-section {
    scroll-margin-top: 82px;
  }

  .legal-section h3 {
    font-size: 20px;
  }

  .legal-section p,
  .legal-section li {
    font-size: 14.5px;
  }

  .legal-back-to-top {
    right: 18px;
    bottom: 18px;
    width: 44px;
    height: 44px;
  }

  .legal-footer__inner {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
